import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

export interface NewTicket {
  id: number;
  title: string;
  ticketNumber: string;
  status: number | null;
  priority: number | null;
  queueID: number | null;
  assignedResourceID: number | null;
  companyID: number | null;
}

/** A personal notification (server/notify.ts): a quote opened / accepted / declined / expiring … */
export interface UserNotice { id: number; kind: string; title: string; body: string; link: string | null; createdAt: string }

interface NotificationContextValue {
  newTickets: NewTicket[];
  /** Unread personal notifications (stored server-side, so they survive being offline). */
  notices: UserNotice[];
  /** Marks one as read (gone from the bell); null = all. */
  readNotice: (id: number | null) => void;
  /** Removes one ticket from the bell. */
  dismissNotification: (id: number) => void;
  clearNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextValue>({
  newTickets: [],
  notices: [],
  readNotice: () => {},
  dismissNotification: () => {},
  clearNotifications: () => {},
});

/**
 * The bell's list is kept per tab in sessionStorage, so a reload doesn't empty it (and dismissed tickets stay
 * dismissed). Closing the tab starts afresh, as before — the server only pushes tickets created while connected.
 */
const STORE_KEY = 'cosmos:notifications';
const MAX = 20;

function loadTickets(): NewTicket[] {
  try {
    const v = JSON.parse(sessionStorage.getItem(STORE_KEY) ?? '[]') as unknown;
    return Array.isArray(v) ? (v as NewTicket[]).filter((t) => typeof t?.id === 'number').slice(0, MAX) : [];
  } catch { return []; }
}

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [newTickets, setNewTickets] = useState<NewTicket[]>(loadTickets);
  const [notices, setNotices] = useState<UserNotice[]>([]);
  const loadNotices = useCallback(() => {
    fetch('/api/notifications').then((r) => (r.ok ? r.json() : null)).then((d: { items?: UserNotice[] } | null) => { if (d?.items) setNotices(d.items); }).catch(() => undefined);
  }, []);
  const esRef = useRef<EventSource | null>(null);
  /** Pending reconnect, cleared on unmount so it can't open an orphaned EventSource. */
  const retryRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Set by the effect cleanup; connect() does nothing once the provider is gone. */
  const disposedRef = useRef(false);

  useEffect(() => {
    try { sessionStorage.setItem(STORE_KEY, JSON.stringify(newTickets)); } catch { /* storage unavailable */ }
  }, [newTickets]);

  const connect = useCallback(() => {
    retryRef.current = null;
    if (disposedRef.current) return;
    const es = new EventSource('/api/notifications/stream');
    esRef.current = es;
    // Anything raised while this tab was away, then live ones as they come.
    es.addEventListener('open', loadNotices);
    es.addEventListener('user-notification', (e: MessageEvent) => {
      try {
        const n = JSON.parse(String(e.data)) as UserNotice;
        setNotices((prev) => (prev.some((x) => x.id === n.id) ? prev : [n, ...prev].slice(0, 30)));
      } catch { /* ignore malformed events */ }
    });

    es.addEventListener('new-ticket', (e: MessageEvent) => {
      try {
        const ticket = JSON.parse(String(e.data)) as NewTicket;
        setNewTickets((prev) => {
          if (prev.some((t) => t.id === ticket.id)) return prev;
          return [ticket, ...prev].slice(0, MAX);
        });
      } catch {
        // ignore malformed events
      }
    });

    es.onerror = () => {
      es.close();
      esRef.current = null;
      if (disposedRef.current) return;
      if (retryRef.current) clearTimeout(retryRef.current);
      retryRef.current = setTimeout(connect, 5_000);
    };
  }, [loadNotices]);

  // A phone PWA in the background loses the stream; reconnect straight away when it comes back rather than waiting
  // for the retry timer (Mobile.md §5).
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || disposedRef.current) return;
      if (esRef.current && esRef.current.readyState !== EventSource.CLOSED) return;
      if (retryRef.current) { clearTimeout(retryRef.current); retryRef.current = null; }
      connect();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [connect]);

  useEffect(() => {
    disposedRef.current = false;
    connect();
    return () => {
      disposedRef.current = true;
      if (retryRef.current) { clearTimeout(retryRef.current); retryRef.current = null; }
      esRef.current?.close();
      esRef.current = null;
    };
  }, [connect]);

  const dismissNotification = useCallback((id: number) => setNewTickets((prev) => prev.filter((t) => t.id !== id)), []);
  const clearNotifications = useCallback(() => setNewTickets([]), []);
  const readNotice = useCallback((id: number | null) => {
    setNotices((prev) => (id == null ? [] : prev.filter((n) => n.id !== id)));
    void fetch('/api/notifications/read', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id }) }).catch(() => undefined);
  }, []);

  return (
    <NotificationContext.Provider value={{ newTickets, notices, readNotice, dismissNotification, clearNotifications }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  return useContext(NotificationContext);
}
